/**
 * Where a raider's `walk` clip is in its cycle (enemyFeel.ts stepUnit), the part that needs no GL. The art measures each
 * walking model's stride from its GLB (art/unitkit/enemy_gait.mjs → art/hq/anchors.json `gait.walk`, the shape the
 * pets use): the ground its planted foot covers in one cycle, at body scale 1. The walks are authored with planted feet
 * (art/unitkit/foot_plant.py), so the clip is driven by DISTANCE, not by time: each frame it advances by the ground the
 * unit covered ÷ its drawn stride, and the planted sole stays locked through slows, hastes, stops and starts. Past a
 * cadence cap the legs would blur, so the clip holds there and the unit leans into a run instead (`runShare`). A model
 * without a gait (a glide: the Necromancer, the flyers) keeps the old time-scaled rule. Pure, so
 * test/enemyGait.test.ts pins it.
 */

/** A walk clip's measured gait: seconds a cycle, and the ground its planted foot covers in one (world units at scale 1). */
export interface WalkGait { readonly cycle: number; readonly stride: number }

export const ENEMY_GAIT = {
  /** An unmeasured model (a glide: the Necromancer, the flyers) keeps the old rule: the clip covers this (cells/s) at rate 1. */
  fallbackSpeed: 1.4,
  /** The unmeasured rule's clip-rate range (the old one). */
  fallbackRate: [0.5, 2.5] as const,
  /** Cycles a second the legs may turn over at most (the bounding imp's 0.23 s cycle hasted stays under it). */
  maxCadence: 5.5,
  /** A root that jumps further than this in one frame (cells: a respawn, a teleport) does not walk the distance. */
  maxStep: 0.5,
  /** The tilt correction (walkCycles) stops growing past this cosine (~60°: a body never tilts further, groundFollow.ts). */
  minTiltCos: 0.5,
  /**
   * How fast a walker turns to its heading (rad/s): at a road corner the sim's heading swings in a tick or two, and a
   * body snapped round its root swept its planted feet across the road (the big units' worst slides).
   */
  turnRate: 7,
  /**
   * The foot lock (FootIk.lock, footIk.ts) holds a planted walker's soles where they stand: while it turns (its facing
   * moved more than `turnEps` rad this frame), while it stands (stopped, casting, blocked), and through a one-shot and
   * `afterShot` s after it (the cross-fade back to the walk), and while its body tilts faster than `tiltRate` (rad/s,
   * pitch and roll) to a change of slope. A straight walk needs none: its clip plants, on a steady slope too (walkCycles),
   * unless its cycle has fewer than `coarseFrames` keys at the clips' `clipFps` (coarseWalk).
   */
  lock: { turnEps: 0.002, afterShot: 0.25, tiltRate: 0.4, coarseFrames: 9, clipFps: 30 },
  /**
   * A planted walker's turn (enemyTurn.ts): a unit younger than `spawnFace` s faces its heading at once; its rounded
   * path steers back onto the sim's line at `steer` rad a cell (at most `steerMax`), catches up what it lags at
   * `catchUp` (1/s) and snaps to the sim's position past `snap` (cells: a jump). It turns no tighter than `radius` of
   * its footprint's half-length (turnRateOf; not under `minRate` rad/s): a long body turned at the full rate on the spot
   * swung its far feet a body-length round (the tortoise's corners slid at 1.8 times its walk).
   */
  turn: { spawnFace: 0.1, catchUp: 3, snap: 0.6, steer: 2.5, steerMax: 0.6, radius: 1, minRate: 1 },
  /** Slower than this (cells/s) a walker stands: setting off from it ends a one-shot still playing (AnimRig.release). */
  standSpeed: 0.05,
  /**
   * Run look: once the cap leaves the planted foot skating at more than `from` of the body's speed, the unit leans
   * further forward and bobs on its footfalls, full at `full` (the share it still skates).
   */
  run: { from: 0.12, full: 0.45, lean: 0.12, bob: 0.035 },
} as const;

/** The measured `walk` gait (anchors.json `gait.walk`), checked; null when the model has none (it glides, or is not measured). */
export function walkGaitOf(measured: unknown): WalkGait | null {
  const g = ((measured ?? {}) as Record<string, Partial<WalkGait> | undefined>).walk;
  return g && typeof g.cycle === "number" && typeof g.stride === "number" && g.cycle > 0 && g.stride > 0 ? { cycle: g.cycle, stride: g.stride } : null;
}

/**
 * A walk keyed in fewer than ENEMY_GAIT.lock.coarseFrames frames a cycle (the imp's 7-frame bound): its sole is exact on
 * the keys only, and the rotations interpolated between them swing it along an arc (a 0.2-0.3 slip of the imp's stance,
 * clipfair.mjs at 120 Hz), so its planted feet are always locked.
 */
export function coarseWalk(g: WalkGait | null): boolean {
  return g !== null && g.cycle * ENEMY_GAIT.lock.clipFps < ENEMY_GAIT.lock.coarseFrames;
}

/** The world units one walk cycle covers as drawn at `scale`; 0 for a model without a gait. */
export function drawnStride(g: WalkGait | null, scale: number): number { return g ? g.stride * scale : 0; }

/**
 * Walk cycles to advance for `dist` covered across the ground (horizontally) in `dt` s by a unit whose cycle covers
 * `stride` (> 0), held under the cadence cap. A body tilted about its own x by `tilt` rad (the slope's pitch,
 * groundFollow.ts bodyTilt: the rares and the bosses, less its lean into the stride; a raider only leans) strides along
 * its own tilted length, so a planted sole crosses stride x cos(tilt) of the ground a cycle: the clip advances by
 * dist / cos(tilt) of the body's stride. (Measured on the Mac, slip6 probe: a rare climbing at a 0.8 rad pitch needs
 * cos 0.73-0.79 where cos(pitch) is 0.70: its lean is in it.)
 */
export function walkCycles(dist: number, dt: number, stride: number, tilt = 0): number {
  if (!(dist > 0) || dist > ENEMY_GAIT.maxStep) return 0;
  const along = dist / Math.max(ENEMY_GAIT.minTiltCos, Math.cos(tilt));
  return Math.min(along / stride, ENEMY_GAIT.maxCadence * dt);
}

/** The time-scaled rule for a model without a gait: the clip rate at `speed` (cells a second). */
export function fallbackRate(speed: number): number {
  const [lo, hi] = ENEMY_GAIT.fallbackRate;
  return Math.min(hi, Math.max(lo, speed / ENEMY_GAIT.fallbackSpeed));
}

/**
 * How much the unit should look like it runs, 0–1: the share of its speed the capped cadence cannot cover (the planted
 * foot's skate) mapped through ENEMY_GAIT.run. 0 for an unmeasured model and for one the clip keeps up with.
 */
export function runShare(speed: number, stride: number): number {
  if (stride <= 0 || speed <= 0) return 0;
  const skate = 1 - Math.min(speed, ENEMY_GAIT.maxCadence * stride) / speed;
  const { from, full } = ENEMY_GAIT.run;
  return Math.min(1, Math.max(0, (skate - from) / (full - from)));
}

/** The facing (rad) a walker turns to from `current` toward `want` in `dt` s, by the shorter way, at `rate` (rad/s). */
export function turnToward(current: number, want: number, dt: number, rate: number = ENEMY_GAIT.turnRate): number {
  const d = Math.atan2(Math.sin(want - current), Math.cos(want - current));
  const step = rate * dt;
  return Math.abs(d) <= step ? want : current + Math.sign(d) * step;
}

/** The rate (rad/s) a planted walker moving at `speed` (world units/s) with a footprint `half` long turns at. */
export function turnRateOf(speed: number, half: number): number {
  const T = ENEMY_GAIT.turn;
  return Math.min(ENEMY_GAIT.turnRate, Math.max(T.minRate, speed / Math.max(1e-6, T.radius * half)));
}

/**
 * The foot lock's weight for a walker this frame, 0 or 1 (FootIk holds a pin until its foot lifts): only a model with a
 * planted walk (a measured stride) locks; it does whenever it is not simply walking straight on, and when `hold`: its
 * body pitches or rolls to a change of slope faster than ENEMY_GAIT.lock.tiltRate (which swung the soles a
 * body-length from the root across the ground: the rares' slope slip in the slip6 probe), or its walk is coarse.
 */
export function footLock(stride: number, moving: boolean, turning: boolean, sinceShot: number, hold = false): number {
  if (stride <= 0) return 0;
  return !moving || turning || hold || sinceShot < ENEMY_GAIT.lock.afterShot ? 1 : 0;
}
